import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as helpAPI from 'api/Help';

const initialState = {
  pageSize: 15,
  page: 1,
  issuesListData: null,
  issueContentData: null,
  count: null,
  ui: {
    loading: false
  },
  selectedIssueId: null,
  search: '',
  dateFilter: null,
  attachments: [],
  error: null,
  status: 'init'
};

const getIssuesListData = createAsyncThunk('help/getIssuesListData', async ({ signal, ...params }, { rejectWithValue }) => {
  try {
    const res = await helpAPI.getIssuesList(params, signal);
    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const getIssueDataByKey = createAsyncThunk('help/getIssueDataByKey', async ({ signal, ...params }, { rejectWithValue }) => {
  try {
    const res = await helpAPI.getIssueDataByKey(params, signal);
    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const createIssue = createAsyncThunk('help/createIssue', async ({ signal, ...params }, { rejectWithValue }) => {
  try {
    const res = await helpAPI.createIssue(params, signal);
    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const addAttachments = createAsyncThunk('help/addAttachments', async ({ signal, ...params }, { rejectWithValue }) => {
  try {
    const res = await helpAPI.addAttachments(params, signal);
    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

function downloadFile(response, fileName) {
  try {
    const blob = new Blob([response.data], {
      type: response?.headers?.['content-type']
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('downloadFile', error);
  }
}

const downloadAttachments = createAsyncThunk('help/downloadAttachments', async ({ signal, ...params }, { rejectWithValue }) => {
  try {
    const { filename, ...req } = params;
    const res = await helpAPI.downloadAttachments(req, signal);
    const fileName = filename || 'download file';
    downloadFile(res, fileName);
    return {};
  } catch (error) {
    return rejectWithValue(error);
  }
});

const getAttachments = createAsyncThunk('help/getAttachments', async ({ signal, ...params }, { rejectWithValue }) => {
  try {
    const { id } = params;
    const { filename, ...req } = params;
    const res = await helpAPI.downloadAttachments(req, signal);
    return { id: id, filename, data: URL.createObjectURL(res?.data), type: res?.data?.type };
  } catch (error) {
    return rejectWithValue(error);
  }
});

const deleteIssue = createAsyncThunk('help/deleteIssue', async ({ signal, ...params }, { rejectWithValue }) => {
  try {
    const res = await helpAPI.deleteIssue(params, signal);
    return res.data?.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const helpSlice = createSlice({
  name: 'help',
  initialState: initialState,

  reducers: {
    resetState: () => {
      return initialState;
    },
    setCurrentPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setSelectedIssueId: (state, action) => {
      state.selectedIssueId = action.payload;
    },
    removeIssues: (state) => {
      state.page = 1;
      state.issuesListData = null;
      state.count = 0;
      state.issueContentData = null;
      state.selectedIssueId = null;
      state.search = '';
      state.attachments = [];
      state.dateFilter = null;
    },
    setSelectedSearch: (state, action) => {
      state.search = action.payload;
    },
    setSelectedDateFilter: (state, action) => {
      state.dateFilter = action.payload;
    }
  },

  extraReducers: {
    [getIssuesListData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [getIssuesListData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const data = action.payload?.data;
      if (state.issuesListData && state.issuesListData?.length && data && state.page !== 1) {
        state.issuesListData = [...state.issuesListData, ...data];
      } else {
        state.issuesListData = action.payload?.data;
        state.attachments = [];
        if (action.payload?.data?.length <= 0) state.issueContentData = null;
      }
      state.count = action.payload?.total_records;
    },

    [getIssuesListData.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getIssueDataByKey.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [getIssueDataByKey.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.issueContentData = action.payload;
      state.attachments = [];
    },

    [getIssueDataByKey.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [createIssue.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createIssue.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createIssue.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteIssue.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteIssue.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteIssue.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [addAttachments.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [addAttachments.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [addAttachments.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [downloadAttachments.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [downloadAttachments.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [downloadAttachments.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getAttachments.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [getAttachments.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.attachments = [...state.attachments, action.payload];
    },

    [getAttachments.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

export const { resetState, setCurrentPage, setPageSize, setSelectedIssueId, removeIssues, setSelectedSearch, setSelectedDateFilter } =
  helpSlice.actions;

export default helpSlice.reducer;

export { getIssuesListData, getIssueDataByKey, createIssue, deleteIssue, addAttachments, downloadAttachments, getAttachments };
